import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate, useParams, useLocation } from 'react-router-dom';
import './App.css';

function App() {
  const [games, setGames] = useState({});
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    // Handle window resize
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      // Close menu when switching to desktop
      if (!mobile && isMenuOpen) {
        setIsMenuOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isMenuOpen]);

  useEffect(() => {
    // Fetch games on mount
    fetchGames();
  }, []);

  const fetchGames = async () => {
    try {
      // Add random query parameter to prevent caching on iOS
      const cacheBuster = `_=${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      const response = await fetch(`/api/games?${cacheBuster}`, {
        cache: 'no-store',
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        }
      });
      if (response.ok) {
        const data = await response.json();
        setGames(data);
      }
    } catch (error) {
      console.error('Error fetching games:', error);
    }
  };

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const gamesList = Object.values(games);

  const getGameImage = (game) => {
    if (game.image && game.image.trim() !== '') {
      // Ensure image path is absolute (starts with /)
      const imagePath = game.image.startsWith('/') ? game.image : `/${game.image}`;
      return imagePath;
    }
    return '/noimage.jpg';
  };

  const getLatestVersion = (game) => {
    if (!game.versions || Object.keys(game.versions).length === 0) {
      return null;
    }
    // Sort versions numerically (not as strings)
    const versions = Object.values(game.versions).sort((a, b) => {
      const versionA = parseInt(a.version) || 0;
      const versionB = parseInt(b.version) || 0;
      return versionB - versionA; // Descending order (latest first)
    });
    return versions[0];
  };


  return (
    <Router>
      <AppContent 
        games={games}
        getGameImage={getGameImage}
        getLatestVersion={getLatestVersion}
        formatDate={formatDate}
        isMobile={isMobile}
        isMenuOpen={isMenuOpen}
        setIsMenuOpen={setIsMenuOpen}
        gamesList={gamesList}
      />
    </Router>
  );
}

function AppContent({ games, getGameImage, getLatestVersion, formatDate, isMobile, isMenuOpen, setIsMenuOpen, gamesList }) {
  const location = useLocation();
  const isHomePage = location.pathname === '/';
  
  // Extract current gameId and version from pathname (e.g., "/bus/1" -> gameId: "bus", version: "1")
  const pathParts = location.pathname.split('/').filter(Boolean);
  const currentGameId = pathParts[0] || null;
  const currentVersion = pathParts[1] || null;

  return (
    <div className="App">
      <header className="app-header">
        <div className="header-left">
          <img src="/logo.png" alt="Logo" className="header-logo" />
          <h1 className="header-title">Games Gallery</h1>
        </div>
        {isMobile && !isHomePage && (
          <button 
            className="burger-menu"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label="Toggle menu"
          >
            <span className={`burger-line ${isMenuOpen ? 'open' : ''}`}></span>
            <span className={`burger-line ${isMenuOpen ? 'open' : ''}`}></span>
            <span className={`burger-line ${isMenuOpen ? 'open' : ''}`}></span>
          </button>
        )}
      </header>

      <div className="app-content">
        <Routes>
          <Route 
            path="/" 
            element={
              <Home 
                games={games}
                getGameImage={getGameImage}
                getLatestVersion={getLatestVersion}
                formatDate={formatDate}
              />
            } 
          />
          <Route path="/:gameId/:version" element={<GameView games={games} />} />
          <Route path="/:gameId" element={<GameView games={games} />} />
        </Routes>

        {/* Desktop sidebar - hide on home page */}
        {!isMobile && !isHomePage && (
          <aside className="games-sidebar">
              <h2 className="sidebar-title">Games</h2>
              <div className="games-list">
                {gamesList.length === 0 ? (
                  <p className="no-games">No games available</p>
                ) : (
                  gamesList.map((game) => {
                    const latestVersion = getLatestVersion(game);
                    const isSelected = currentGameId === game.id;
                    
                    return (
                      <GameItem
                        key={game.id}
                        game={game}
                        latestVersion={latestVersion}
                        getGameImage={getGameImage}
                        formatDate={formatDate}
                        isMobile={isMobile}
                        setIsMenuOpen={setIsMenuOpen}
                        isSelected={isSelected}
                        getLatestVersion={getLatestVersion}
                        currentVersion={currentGameId === game.id ? currentVersion : null}
                      />
                    );
                  })
                )}
              </div>
            </aside>
          )}

          {/* Mobile menu overlay */}
          {isMobile && (
            <>
              {isMenuOpen && (
                <div className="menu-overlay" onClick={() => setIsMenuOpen(false)}></div>
              )}
              <aside className={`mobile-menu ${isMenuOpen ? 'open' : ''}`}>
                <h2 className="sidebar-title">Games</h2>
                <div className="games-list">
                  {gamesList.length === 0 ? (
                    <p className="no-games">No games available</p>
                  ) : (
                    gamesList.map((game) => {
                      const latestVersion = getLatestVersion(game);
                      const isSelected = currentGameId === game.id;
                      
                      return (
                        <GameItem
                          key={game.id}
                          game={game}
                          latestVersion={latestVersion}
                          getGameImage={getGameImage}
                          formatDate={formatDate}
                          isMobile={isMobile}
                          setIsMenuOpen={setIsMenuOpen}
                          isSelected={isSelected}
                          getLatestVersion={getLatestVersion}
                          currentVersion={currentGameId === game.id ? currentVersion : null}
                        />
                      );
                    })
                  )}
                </div>
              </aside>
            </>
          )}
        </div>
      </div>
  );
}

function Home({ games, getGameImage, getLatestVersion, formatDate }) {
  const gamesList = Object.values(games);

  return (
    <main className="main-content home-gallery">
      <h2 className="gallery-header">All games</h2>
      {gamesList.length === 0 ? (
        <div className="no-games-message">
          <p>No games available</p>
        </div>
      ) : (
        <div className="games-grid">
          {gamesList.map((game) => {
            const latestVersion = getLatestVersion(game);
            
            return (
              <GameItem
                key={game.id}
                game={game}
                latestVersion={latestVersion}
                getGameImage={getGameImage}
                formatDate={formatDate}
                isMobile={false}
                setIsMenuOpen={() => {}}
              />
            );
          })}
        </div>
      )}
    </main>
  );
}

function GameItem({ game, latestVersion, getGameImage, formatDate, isMobile, setIsMenuOpen, isSelected, getLatestVersion, currentVersion }) {
  const navigate = useNavigate();
  const [showVersionPopup, setShowVersionPopup] = useState(false);

  const handleItemClick = (e) => {
    // Don't navigate if clicking on version overlay
    if (e.target.classList.contains('game-version-overlay')) {
      e.stopPropagation();
      return;
    }
    
    if (isMobile) {
      setIsMenuOpen(false);
    }
    if (latestVersion) {
      navigate(`/${game.id}/${latestVersion.version}`);
    } else {
      navigate(`/${game.id}`);
    }
  };

  const handleVersionClick = (e) => {
    e.stopPropagation();
    setShowVersionPopup(true);
  };

  const handleVersionSelect = (version) => {
    setShowVersionPopup(false);
    if (isMobile) {
      setIsMenuOpen(false);
    }
    navigate(`/${game.id}/${version.version}`);
  };

  const handleClosePopup = (e) => {
    if (e.target === e.currentTarget) {
      setShowVersionPopup(false);
    }
  };

  const getAllVersions = () => {
    if (!game.versions || Object.keys(game.versions).length === 0) {
      return [];
    }
    // Sort versions numerically (descending - latest first)
    return Object.values(game.versions).sort((a, b) => {
      const versionA = parseInt(a.version) || 0;
      const versionB = parseInt(b.version) || 0;
      return versionB - versionA;
    });
  };

  const versions = getAllVersions();

  // Get current version to display (use currentVersion if available, otherwise latest)
  const getCurrentVersionToDisplay = () => {
    if (!latestVersion) return null;
    
    // If we have a current version, find it; otherwise use latest
    if (currentVersion && game.versions && game.versions[currentVersion]) {
      return game.versions[currentVersion];
    }
    return latestVersion;
  };

  const displayVersion = getCurrentVersionToDisplay();
  const isNotLatest = currentVersion && latestVersion && currentVersion !== latestVersion.version;

  return (
    <>
      <div className={`game-item ${isSelected ? 'selected' : ''}`} onClick={handleItemClick}>
        <div 
          className="game-image"
          style={{ backgroundImage: `url(${getGameImage(game)})` }}
        >
          <div className="game-name-overlay">{game.name || game.id}</div>
          {displayVersion && (
            <>
              <div 
                className={`game-version-overlay ${isNotLatest ? 'not-latest' : ''}`} 
                onClick={handleVersionClick}
              >
                v{displayVersion.version}
              </div>
              <div className="game-date-overlay">{formatDate(displayVersion.timestamp)}</div>
            </>
          )}
        </div>
      </div>

      {showVersionPopup && (
        <VersionPopup
          game={game}
          versions={versions}
          formatDate={formatDate}
          onSelect={handleVersionSelect}
          onClose={handleClosePopup}
          currentVersion={currentVersion}
        />
      )}
    </>
  );
}

function GameView({ games }) {
  const { gameId, version } = useParams();
  const navigate = useNavigate();
  const [localGames, setLocalGames] = useState(games);
  const [loading, setLoading] = useState(Object.keys(games).length === 0);

  useEffect(() => {
    // Fetch games if not loaded
    if (Object.keys(localGames).length === 0) {
      fetchGames();
    }
  }, []);

  const fetchGames = async () => {
    try {
      // Add random query parameter to prevent caching on iOS
      const cacheBuster = `_=${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      const response = await fetch(`/api/games?${cacheBuster}`, {
        cache: 'no-store',
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        }
      });
      if (response.ok) {
        const data = await response.json();
        setLocalGames(data);
        setLoading(false);
      }
    } catch (error) {
      console.error('Error fetching games:', error);
      setLoading(false);
    }
  };

  const game = localGames[gameId];

  const getLatestVersion = (game) => {
    if (!game.versions || Object.keys(game.versions).length === 0) {
      return null;
    }
    // Sort versions numerically (not as strings)
    const versions = Object.values(game.versions).sort((a, b) => {
      const versionA = parseInt(a.version) || 0;
      const versionB = parseInt(b.version) || 0;
      return versionB - versionA; // Descending order (latest first)
    });
    return versions[0];
  };

  useEffect(() => {
    // If no version specified, redirect to latest version
    if (game && !version) {
      const latestVersion = getLatestVersion(game);
      if (latestVersion) {
        navigate(`/${gameId}/${latestVersion.version}`, { replace: true });
      }
    }
  }, [game, gameId, version, navigate]);

  if (loading) {
    return (
      <main className="main-content">
        <div className="game-error">Loading...</div>
      </main>
    );
  }

  if (!game) {
    return (
      <main className="main-content">
        <div className="game-error">Game not found</div>
      </main>
    );
  }

  const gameVersion = version || (getLatestVersion(game)?.version);
  if (!gameVersion) {
    return (
      <main className="main-content">
        <div className="game-error">No version available for this game</div>
      </main>
    );
  }

  const gameUrl = `/games/${gameId}/${gameVersion}/index.html`;

  return (
    <main className="main-content game-view">
      <iframe
        src={gameUrl}
        className="game-iframe"
        title={`${game.name || gameId} - v${gameVersion}`}
        allow="fullscreen"
        allowFullScreen
      />
    </main>
  );
}

function VersionPopup({ game, versions, formatDate, onSelect, onClose, currentVersion }) {
  return (
    <div className="version-popup-overlay" onClick={onClose}>
      <div className="version-popup" onClick={(e) => e.stopPropagation()}>
        <div className="version-popup-header">
          <h3>{game.name || game.id} - Versions</h3>
          <button className="version-popup-close" onClick={onClose}>×</button>
        </div>
        <div className="version-popup-content">
          {versions.length === 0 ? (
            <p className="no-versions">No versions available</p>
          ) : (
            <div className="version-list">
              {versions.map((version) => {
                const isCurrentVersion = currentVersion === version.version;
                return (
                  <div
                    key={version.version}
                    className={`version-item ${isCurrentVersion ? 'current' : ''}`}
                    onClick={() => onSelect(version)}
                  >
                    <div className="version-item-number">v{version.version}</div>
                    <div className="version-item-date">{formatDate(version.timestamp)}</div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default App;
